import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { IWorkflowRun } from '@care-portals/api-interfaces';
import { WorkflowRunDetailsViewComponent } from '@care-portals/ui';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { CRMService } from '../../../services/api/crm.service';

/**
 * EMR host for the shared workflow run inspector. Loads the full run from the
 * CRM API on demand and wires rerun — same role the CRM list page handles inline.
 */
@Component({
  selector: 'portals-emr-workflow-run-detail-drawer',
  standalone: true,
  imports: [
    CommonModule,
    NzDrawerModule,
    NzIconModule,
    WorkflowRunDetailsViewComponent
  ],
  templateUrl: './workflow-run-detail-drawer.component.html',
  styleUrls: ['./workflow-run-detail-drawer.component.less']
})
export class WorkflowRunDetailDrawerComponent implements OnChanges {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @Input() workflowRunId: string | null = null;

  /** Emits the run id once a rerun succeeds, so the host can drop it from the
   * failed-runs history (a reran run is no longer an open failure). */
  @Output() reran = new EventEmitter<string>();

  loading = false;
  run: IWorkflowRun | null = null;
  rerunning = false;

  constructor(
    private readonly crmService: CRMService,
    private readonly notification: NzNotificationService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && !this.visible) {
      this.run = null;
      this.rerunning = false;
      this.loading = false;
      return;
    }

    if (
      this.visible &&
      this.workflowRunId &&
      (changes['visible'] || changes['workflowRunId'])
    ) {
      this.loadRun(this.workflowRunId);
    }
  }

  onClose(): void {
    this.visibleChange.emit(false);
  }

  onRerun(): void {
    const run = this.run;
    if (!run?._id || this.rerunning) {
      return;
    }
    this.rerunning = true;
    this.crmService.rerunWorkflowRun(run._id).subscribe({
      next: () => {
        this.rerunning = false;
        if (this.run) {
          this.run = { ...this.run, reranAt: new Date() };
        }
        this.reran.emit(run._id);
        this.notification.success('Workflow rerun started', '');
      },
      error: err => {
        this.rerunning = false;
        this.notification.error(
          'Rerun failed',
          err?.status === 403
            ? 'You do not have permission to rerun workflows.'
            : 'Could not rerun the workflow. Please try again.'
        );
      }
    });
  }

  private loadRun(id: string): void {
    this.run = null;
    this.rerunning = false;
    this.loading = true;
    this.crmService.getWorkflowRun(id).subscribe({
      next: detail => {
        this.run = detail;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.notification.error(
          'Could not load workflow run',
          'Please try again.'
        );
        this.onClose();
      }
    });
  }
}
